import { z } from 'zod';
import {
  RATE_MODE_OPTIONS,
  rateModeDefaults,
  rateModeFields,
  refineRateMode,
} from '../../../playground/endpoints/_shared';
import {
  COMPENSATION_PLAN_OPTIONS,
  GENDER_OPTIONS,
  PAYMENT_PERIODICITY_OPTIONS,
  SAVINGS_MODE_OPTIONS,
  contributionSchema,
} from '../_shared';

export {
  COMPENSATION_PLAN_OPTIONS,
  GENDER_OPTIONS,
  PAYMENT_PERIODICITY_OPTIONS,
  RATE_MODE_OPTIONS,
  SAVINGS_MODE_OPTIONS,
};

/**
 * UI-only selector, stripped before sending. In `savings_capacity` the premium
 * is fixed and the engine solves what fraction of an objective it can fund, so
 * this picks whether an objective travels at all.
 *
 * Deliberately narrower than v1's equivalent, which also offered periodic
 * withdrawals: v2's `simulation/modes/savings_capacity.py` only ever solves a
 * single lump-sum goal, and silently ignores `withdrawal_amount`.
 */
export const CAPACITY_OBJECTIVE_OPTIONS = [
  { value: 'none', label: 'Sin objetivo (solo proyección)' },
  { value: 'goal', label: 'Objetivo único' },
] as const;

/**
 * `timeframe` is optional on `FamilySavingsInput`: left out, the engine fills
 * it with the maximum projection below the permanence age. An untouched number
 * input hands react-hook-form `''`, which `z.coerce.number()` would turn into
 * 0, so blank is normalised to absent before coercion.
 */
const optionalTimeframe = z
  .preprocess(
    (value) => (value === '' || value === null ? undefined : value),
    z.coerce.number().int().optional(),
  )
  .optional();

// Types and required-ness only: every business rule (capital bounds, coverage
// ratios, entry ages, premium floors, horizon limits) is the engine's to
// enforce and to report, so this page stays a thin client over the API.
const sharedFields = {
  date_of_birth: z
    .string()
    .min(1, 'Requerido')
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Formato YYYY-MM-DD'),
  gender: z.enum(['MALE', 'FEMALE', 'OTHER']),
  compensation_plan: z.enum(['A', 'B']),
  payment_periodicity: z.enum(['monthly', 'quarterly', 'semiannual', 'annual']),
  ...rateModeFields,
  timeframe: optionalTimeframe,
  contributions: z.array(contributionSchema),
  death_insured_capital: z.coerce.number(),
  accidental_death_insured_capital: z.coerce.number(),
  itp_insured_capital: z.coerce.number(),
  serious_illness_insured_capital: z.coerce.number(),
};

// Which fields each mode carries -- the client-side mirror of
// `validators/registry.MODE_RULES`, used to drive field visibility.
export const familySavingsV2Schema = z
  .discriminatedUnion('savings_mode', [
    z.object({
      ...sharedFields,
      savings_mode: z.literal('goal_value'),
      goal: z.coerce.number(),
      goal_horizon_month: z.coerce.number().int(),
    }),
    z.object({
      ...sharedFields,
      savings_mode: z.literal('savings_capacity'),
      // `.default('none')` guards submit against an untouched select value.
      capacity_objective: z.enum(['none', 'goal']).default('none'),
      agreed_premium: z.coerce.number(),
      goal: z.coerce.number().optional(),
      goal_horizon_month: z.coerce.number().int().optional(),
    }),
    z.object({
      ...sharedFields,
      savings_mode: z.literal('withdrawal_goal'),
      withdrawal_amount: z.coerce.number(),
      withdrawal_period_months: z.coerce.number().int(),
    }),
  ])
  // The only cross-field rule kept: `rate_mode` is a UI toggle deciding which
  // of two mutually exclusive fields the payload carries, so the form has to
  // resolve it before a request can be built at all.
  .superRefine(refineRateMode);

export type FamilySavingsV2FormValues = z.infer<typeof familySavingsV2Schema>;

const sharedDefaults = {
  date_of_birth: '1990-01-01' as const,
  gender: 'MALE' as const,
  compensation_plan: 'A' as const,
  payment_periodicity: 'monthly' as const,
  ...rateModeDefaults,
  projection_rate: 0.02,
  contributions: [],
  // Seeds the savings_capacity-only fields so they survive mode switches via
  // AutoForm's `keepDefaultValue` unregister.
  capacity_objective: 'none' as const,
  agreed_premium: 2,
  death_insured_capital: 300,
  accidental_death_insured_capital: 0,
  itp_insured_capital: 0,
  serious_illness_insured_capital: 0,
};

export const familySavingsV2Defaults: FamilySavingsV2FormValues = {
  ...sharedDefaults,
  savings_mode: 'goal_value',
  goal: 500,
  goal_horizon_month: 120,
};
